import { canvasToBlob, createCanvas, createImage } from '@/utils/dom';
import type { CanvasCapture } from '@/types/files';

type MinMax = number | undefined;

export const PREVIEW_SIZE = 250;

export function scaleImage(
  img: HTMLImageElement,
  width: number,
  height: number
) {
  const { canvas, ctx } = createCanvas(width, height);
  ctx.drawImage(img, 0, 0, width, height);
  return createImage(canvas.toDataURL('image/png'));
}

export async function scaleImageToFit(
  img: HTMLImageElement,
  maxWidth: number,
  maxHeight?: number
) {
  // Can leave maxHeight undefined if same as maxWidth
  let width = maxWidth;
  let height = maxHeight ?? maxWidth;
  if (img.width <= width && img.height <= height) {
    return img; // Already fits within constraints
  }
  const aspectRatio = img.width / img.height;

  if (img.width > img.height) {
    // Landscape
    height = maxWidth / aspectRatio;
  } else {
    // Portrait
    width = height * aspectRatio;
  }

  return scaleImage(img, width, height);
}

export async function createPreviewImage(src: string): Promise<Blob> {
  // Scale an image to fit preview size
  const img = await scaleImageToFit(await createImage(src), PREVIEW_SIZE);
  const { canvas, ctx } = createCanvas(img.width, img.height);
  ctx.drawImage(img, 0, 0, img.width, img.height);
  return canvasToBlob(canvas);
}

export async function trimTransparentEdges(
  img: HTMLImageElement
): Promise<CanvasCapture> {
  // Inspired by https://stackoverflow.com/a/22267731
  let index = 0;
  let minX: MinMax, minY: MinMax, maxX: MinMax, maxY: MinMax;
  let { canvas, ctx } = createCanvas(img.width, img.height, true);
  ctx.drawImage(img, 0, 0, img.width, img.height);
  let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  for (let y = 0; y < canvas.height; y++) {
    for (let x = 0; x < canvas.width; x++) {
      index = (y * canvas.width + x) * 4;
      if (imageData.data[index + 3] > 0) {
        if (minX === undefined || x < minX) minX = x;
        else if (maxX === undefined || x > maxX) maxX = x;

        if (minY === undefined || y < minY) minY = y;
        else if (maxY === undefined || y > maxY) maxY = y;
      }
    }
  }
  const left = minX || 0;
  const top = minY || 0;
  const width = maxX !== undefined && minX !== undefined ? maxX - minX : 0;
  const height = maxY !== undefined && minY !== undefined ? maxY - minY : 0;
  const cut = ctx.getImageData(left, top, width, height);
  canvas.width = width;
  canvas.height = height;
  ctx.putImageData(cut, 0, 0);
  const blob = await canvasToBlob(canvas);
  return {
    blob,
    width,
    height,
    left,
    top,
  };
}
